import React, { useState, useMemo, useCallback, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import { Settings, Layers } from 'lucide-react';
import { useSelectedMetrics, getCosClassName, getCpaClassName, useComponentMetrics } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

import { calculateVariation, renderVariation } from '../utils/variationUtils';
import { colonnesVideoGoogleAds, cumulerVideo, TOTAUX_VIDEO_INITIAUX } from '../utils/metriquesVideo';
import { getDateRangeFromPeriod, calculatePreviousPeriod } from '../utils/periodUtils';
/**
 * Composant table modulaire des groupes de composants Performance Max
 * Optimisé avec React.memo et useMemo pour les performances
 */
const AssetGroupsTable = React.memo(({
  rawData,
  selectedAccount,
  isVisible = true,
  onToggleVisibility,
  formatNumber,
  selectedCampaignFilter,
  selectedCampaignTypeFilter,
  selectedLabelsFilter = [],
  campaignMatchesLabelsFilter = () => true,
  // Props pour comparaison de périodes
  previousPeriodData = null,
  compareEnabled = false,
  comparisonType = '',
  loadingComparison = false,
  // Props additionnelles pour charger les données de période précédente
  token = null,
  selectedPeriod = 'LAST_30_DAYS',
  customStartDate = null,
  customEndDate = null,
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'asset_groups'
}) => {
  // États locaux pour cette table
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  // États pour les données de période précédente (chargées via API)
  const [loadedPreviousAssetGroups, setLoadedPreviousAssetGroups] = useState([]);
  const [loadingPrevious, setLoadingPrevious] = useState(false);

  // Hook pour les métriques sélectionnées
  const selectedMetrics = useSelectedMetrics(selectedAccount?.id || 'global');

  // Hook pour les métriques du composant
  const componentMetrics = useComponentMetrics('asset_groups', selectedAccount?.id || 'global');

  // Helper pour vérifier si une métrique est sélectionnée pour ce composant
  const isComponentMetricSelected = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  /**
   * Vidéo TrueView : donnée à part, et payée seulement si elle est demandée.
   *
   * Le gros appel d'analyse qui alimente `rawData` n'interroge pas `asset_group_asset`, seule
   * ressource à servir ces métriques au niveau d'un groupe de composants. On va donc les
   * chercher ici, et uniquement si l'utilisateur a coché l'une des trois colonnes : deux
   * requêtes API de plus, inutiles pour qui ne s'en sert pas.
   */
  const metriquesVideoActives = useMemo(
    () => ['views', 'vtr', 'cpv'].some(cle => componentMetrics.metrics.includes(cle)),
    [componentMetrics.metrics]
  );
  const [donneesVideo, setDonneesVideo] = useState({ parGroupe: {}, nonAttribuee: null });

  useEffect(() => {
    if (!metriquesVideoActives || !selectedAccount?.id || !token) {
      setDonneesVideo({ parGroupe: {}, nonAttribuee: null });
      return;
    }

    let annule = false;
    (async () => {
      try {
        const reponse = await axios.post(`${config.API_URL}/api/dashboard/components/asset-groups`, {
          sources: [selectedAccount.id],
          period: selectedPeriod,
          customStartDate,
          customEndDate,
          inclureVideo: true
        }, { headers: { Authorization: `Bearer ${token}` } });

        if (annule || !reponse.data?.success) return;

        const groupes = reponse.data.data?.assetGroups || [];
        const parGroupe = {};
        groupes.forEach(groupe => {
          parGroupe[`${groupe.name}_${(groupe.campaignIds || []).join('|')}`] = {
            views: groupe.views || 0,
            videoImpressions: groupe.videoImpressions || 0,
            viewCost: groupe.viewCost || 0
          };
          // Repli sur le seul nom : les identifiants de campagne peuvent différer d'un appel
          // à l'autre selon les filtres appliqués.
          parGroupe[groupe.name] = parGroupe[groupe.name] || {
            views: groupe.views || 0,
            videoImpressions: groupe.videoImpressions || 0,
            viewCost: groupe.viewCost || 0
          };
        });

        setDonneesVideo({ parGroupe, nonAttribuee: reponse.data.data?.videoNonAttribuee || null });
      } catch (erreur) {
        console.warn('⚠️ [ASSET-GROUPS] Métriques vidéo indisponibles:', erreur.message);
        if (!annule) setDonneesVideo({ parGroupe: {}, nonAttribuee: null });
      }
    })();

    return () => { annule = true; };
  }, [metriquesVideoActives, selectedAccount?.id, token, selectedPeriod, customStartDate, customEndDate]);



  // Charger les données de la période précédente si comparaison activée
  useEffect(() => {
    if (!isVisible) return;

    console.log('🎭 [ASSET-GROUPS] useEffect déclenché avec:', {
      compareEnabled,
      comparisonType,
      selectedAccountId: selectedAccount?.id,
      hasToken: !!token,
      selectedPeriod,
      customStartDate,
      customEndDate
    });

    const loadPreviousData = async () => {
      if (!compareEnabled || !comparisonType || !selectedAccount?.id || !token) {
        console.log('🎭 [ASSET-GROUPS] Conditions non remplies pour charger les données:', {
          compareEnabled,
          comparisonType,
          selectedAccountId: selectedAccount?.id,
          hasToken: !!token
        });
        if (!compareEnabled) {
          setLoadedPreviousAssetGroups([]);
        }
        return;
      }

      setLoadingPrevious(true);

      let currentStartDate = customStartDate;
      let currentEndDate = customEndDate;
      if (!currentStartDate || !currentEndDate) {
        const dateRange = getDateRangeFromPeriod(selectedPeriod);
        currentStartDate = dateRange.startDate;
        currentEndDate = dateRange.endDate;
      }

      const prevPeriod = calculatePreviousPeriod(currentStartDate, currentEndDate, comparisonType);
      console.log(`🎭 [ASSET-GROUPS] Chargement période précédente: ${prevPeriod.startDate} à ${prevPeriod.endDate}`);

      try {
        const response = await axios.post(`${config.API_URL}/api/dashboard/components/asset-groups`, {
          sources: [selectedAccount.id],
          period: 'CUSTOM',
          customStartDate: prevPeriod.startDate,
          customEndDate: prevPeriod.endDate,
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        console.log('🎭 [ASSET-GROUPS] Réponse API:', response.data);
        if (response.data?.success) {
          const prevAssetGroups = response.data.data?.assetGroups || response.data.assetGroups || [];
          console.log(`🎭 [ASSET-GROUPS] ${prevAssetGroups.length} groupes de composants période précédente:`, prevAssetGroups);
          setLoadedPreviousAssetGroups(prevAssetGroups);
        } else {
          console.warn('🎭 [ASSET-GROUPS] Réponse API non success:', response.data);
        }
      } catch (error) {
        console.warn(`⚠️ [ASSET-GROUPS] Erreur chargement période précédente:`, error.message);
        setLoadedPreviousAssetGroups([]);
      } finally {
        setLoadingPrevious(false);
      }
    };

    loadPreviousData();
  }, [isVisible, compareEnabled, comparisonType, customStartDate, customEndDate, selectedAccount?.id, token, selectedPeriod]);



  // Configuration du rendu des métriques (utilisé pour l'ordre dynamique)
  const metricConfig = useMemo(() => ({
    impressions: {
      label: 'Impressions',
      sortKey: 'impressions',
      renderCell: (assetGroup, prevAssetGroup, hasComparison) => (
        <>
          <div>{formatNumber(assetGroup.impressions || 0)}</div>
          {hasComparison && renderVariation(calculateVariation(assetGroup.impressions || 0, prevAssetGroup.impressions || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.impressions)}</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.impressions, previousTotals.impressions))}
        </>
      )
    },
    ctr: {
      label: 'CTR',
      sortKey: 'ctr',
      renderCell: (assetGroup, prevAssetGroup, hasComparison) => (
        <>
          <div>{formatNumber(assetGroup.ctr || 0, 2)}%</div>
          {hasComparison && renderVariation(calculateVariation(assetGroup.ctr || 0, prevAssetGroup.ctr || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.ctr, 2)}%</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.ctr, previousTotals.ctr))}
        </>
      )
    },
    clics: {
      label: 'Clics',
      sortKey: 'clicks',
      renderCell: (assetGroup, prevAssetGroup, hasComparison) => (
        <>
          <div>{formatNumber(assetGroup.clicks || 0)}</div>
          {hasComparison && renderVariation(calculateVariation(assetGroup.clicks || 0, prevAssetGroup.clicks || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.clicks)}</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.clicks, previousTotals.clicks))}
        </>
      )
    },
    cpc: {
      label: 'CPC',
      sortKey: 'cpc',
      renderCell: (assetGroup, prevAssetGroup, hasComparison) => (
        <>
          <div>€{formatNumber(assetGroup.cpc || 0, 2)}</div>
          {hasComparison && renderVariation(calculateVariation(assetGroup.cpc || 0, prevAssetGroup.cpc || 0), true)}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>€{formatNumber(totals.cpc, 2)}</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.cpc, previousTotals.cpc), true)}
        </>
      )
    },
    cout: {
      label: 'Coût',
      sortKey: 'cost',
      renderCell: (assetGroup, prevAssetGroup, hasComparison) => (
        <>
          <div>€{formatNumber(assetGroup.cost || 0, 2)}</div>
          {hasComparison && renderVariation(calculateVariation(assetGroup.cost || 0, prevAssetGroup.cost || 0), true)}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>€{formatNumber(totals.cost, 2)}</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.cost, previousTotals.cost), true)}
        </>
      )
    },
    taux_conv: {
      label: 'Taux conv.',
      sortKey: 'conversionRate',
      renderCell: (assetGroup, prevAssetGroup, hasComparison) => {
        const conversionRate = assetGroup.conversionRate || (assetGroup.clicks > 0 ? (assetGroup.conversions / assetGroup.clicks * 100) : 0);
        const prevConversionRate = prevAssetGroup ? (prevAssetGroup.conversionRate || (prevAssetGroup.clicks > 0 ? (prevAssetGroup.conversions / prevAssetGroup.clicks * 100) : 0)) : 0;
        return (
          <>
            <div>{formatNumber(conversionRate, 2)}%</div>
            {hasComparison && renderVariation(calculateVariation(conversionRate, prevConversionRate))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.conversionRate, 2)}%</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.conversionRate, previousTotals.conversionRate))}
        </>
      )
    },
    conv: {
      label: 'Conv.',
      sortKey: 'conversions',
      renderCell: (assetGroup, prevAssetGroup, hasComparison) => (
        <>
          <div>{formatNumber(assetGroup.conversions || 0, 1)}</div>
          {hasComparison && renderVariation(calculateVariation(assetGroup.conversions || 0, prevAssetGroup.conversions || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.conversions, 1)}</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.conversions, previousTotals.conversions))}
        </>
      )
    },
    cpa: {
      label: 'CPA',
      sortKey: 'cpa',
      getClassName: (assetGroup) => {
        const cpa = assetGroup.cpa || (assetGroup.conversions > 0 ? (assetGroup.cost / assetGroup.conversions) : 0);
        return getCpaClassName(cpa, selectedAccount?.id || 'global', moduleId);
      },
      getTotalClassName: (totals) => getCpaClassName(totals.cpa, selectedAccount?.id || 'global', moduleId),
      renderCell: (assetGroup, prevAssetGroup, hasComparison) => {
        const cpa = assetGroup.cpa || (assetGroup.conversions > 0 ? (assetGroup.cost / assetGroup.conversions) : 0);
        const prevCpa = prevAssetGroup ? (prevAssetGroup.cpa || (prevAssetGroup.conversions > 0 ? (prevAssetGroup.cost / prevAssetGroup.conversions) : 0)) : 0;
        return (
          <>
            <div>{cpa >= 0 ? `€${formatNumber(cpa, 2)}` : '-'}</div>
            {hasComparison && renderVariation(calculateVariation(cpa, prevCpa), true)}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{totals.cpa >= 0 ? `€${formatNumber(totals.cpa, 2)}` : '-'}</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.cpa, previousTotals.cpa), true)}
        </>
      )
    },
    valeur_conv: {
      label: 'Valeur Conv.',
      sortKey: 'conversionsValue',
      renderCell: (assetGroup, prevAssetGroup, hasComparison) => (
        <>
          <div>€{formatNumber(assetGroup.conversionsValue || 0, 2)}</div>
          {hasComparison && renderVariation(calculateVariation(assetGroup.conversionsValue || 0, prevAssetGroup.conversionsValue || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>€{formatNumber(totals.conversionsValue, 2)}</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.conversionsValue, previousTotals.conversionsValue))}
        </>
      )
    },
    cos: {
      label: 'COS',
      sortKey: 'cos',
      getClassName: (assetGroup) => {
        const cos = assetGroup.cos || (assetGroup.conversionsValue > 0 ? (assetGroup.cost / assetGroup.conversionsValue * 100) : 0);
        return getCosClassName(cos, selectedAccount?.id || 'global', moduleId);
      },
      getTotalClassName: (totals) => getCosClassName(totals.cos, selectedAccount?.id || 'global', moduleId),
      renderCell: (assetGroup, prevAssetGroup, hasComparison) => {
        const cos = assetGroup.cos || (assetGroup.conversionsValue > 0 ? (assetGroup.cost / assetGroup.conversionsValue * 100) : 0);
        const prevCos = prevAssetGroup ? (prevAssetGroup.cos || (prevAssetGroup.conversionsValue > 0 ? (prevAssetGroup.cost / prevAssetGroup.conversionsValue * 100) : 0)) : 0;
        return (
          <>
            <div>{formatNumber(cos, 1)}%</div>
            {hasComparison && renderVariation(calculateVariation(cos, prevCos), true)}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.cos, 1)}%</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.cos, previousTotals.cos), true)}
        </>
      )
    },
    panier_moyen: {
      label: 'Panier moyen',
      sortKey: 'panierMoyen',
      renderCell: (assetGroup, prevAssetGroup, hasComparison) => {
        const panierMoyen = (assetGroup.conversions || 0) > 0 ? (assetGroup.conversionsValue || 0) / assetGroup.conversions : 0;
        const prevPanierMoyen = prevAssetGroup && (prevAssetGroup.conversions || 0) > 0 ? (prevAssetGroup.conversionsValue || 0) / prevAssetGroup.conversions : 0;
        return (
          <>
            <div>{panierMoyen >= 0 ? `€${formatNumber(panierMoyen, 2)}` : '-'}</div>
            {hasComparison && renderVariation(calculateVariation(panierMoyen, prevPanierMoyen))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => {
        const panierMoyen = (totals.conversions || 0) > 0 ? (totals.conversionsValue || 0) / totals.conversions : 0;
        const prevPanierMoyen = previousTotals && (previousTotals.conversions || 0) > 0 ? (previousTotals.conversionsValue || 0) / previousTotals.conversions : 0;
        return (
          <>
            <div><strong>{panierMoyen >= 0 ? `€${formatNumber(panierMoyen, 2)}` : '-'}</strong></div>
            {previousTotals && renderVariation(calculateVariation(panierMoyen, prevPanierMoyen))}
          </>
        );
      }
    },
    roas: {
      label: 'ROAS',
      sortKey: 'roas',
      renderCell: (assetGroup, prevAssetGroup, hasComparison) => {
        const roas = (assetGroup.cost || 0) > 0 ? ((assetGroup.conversionsValue || 0) / assetGroup.cost) * 100 : 0;
        const prevRoas = prevAssetGroup && (prevAssetGroup.cost || 0) > 0 ? ((prevAssetGroup.conversionsValue || 0) / prevAssetGroup.cost) * 100 : 0;
        return (
          <>
            <div>{roas >= 0 ? `${formatNumber(roas, 0)}%` : '-'}</div>
            {hasComparison && renderVariation(calculateVariation(roas, prevRoas))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => {
        const roas = (totals.cost || 0) > 0 ? ((totals.conversionsValue || 0) / totals.cost) * 100 : 0;
        const prevRoas = previousTotals && (previousTotals.cost || 0) > 0 ? ((previousTotals.conversionsValue || 0) / previousTotals.cost) * 100 : 0;
        return (
          <>
            <div><strong>{roas >= 0 ? `${formatNumber(roas, 0)}%` : '-'}</strong></div>
            {previousTotals && renderVariation(calculateVariation(roas, prevRoas))}
          </>
        );
      }
    },
    roi: {
      label: 'ROI',
      sortKey: 'roi',
      renderCell: (assetGroup, prevAssetGroup, hasComparison) => {
        const hasData = (assetGroup.cost || 0) > 0 && (assetGroup.conversionsValue || 0) > 0;
        const roi = hasData ? ((assetGroup.conversionsValue || 0) - assetGroup.cost) / assetGroup.cost : 0;
        const prevHasData = (prevAssetGroup?.cost || 0) > 0 && (prevAssetGroup?.conversionsValue || 0) > 0;
        const prevRoi = prevHasData ? ((prevAssetGroup.conversionsValue || 0) - prevAssetGroup.cost) / prevAssetGroup.cost : 0;
        return (
          <>
            <div>{formatNumber(roi, 2)}</div>
            {hasComparison && renderVariation(calculateVariation(roi, prevRoi))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => {
        const hasData = (totals.cost || 0) > 0 && (totals.conversionsValue || 0) > 0;
        const roi = hasData ? ((totals.conversionsValue || 0) - totals.cost) / totals.cost : 0;
        const prevHasData = (previousTotals?.cost || 0) > 0 && (previousTotals?.conversionsValue || 0) > 0;
        const prevRoi = prevHasData ? ((previousTotals.conversionsValue || 0) - previousTotals.cost) / previousTotals.cost : 0;
        return (
          <>
            <div><strong>{formatNumber(roi, 2)}</strong></div>
            {previousTotals && renderVariation(calculateVariation(roi, prevRoi))}
          </>
        );
      }
    },
    ...colonnesVideoGoogleAds({ formatNumber, renderVariation, calculateVariation })
  }), [formatNumber, selectedAccount?.id, renderVariation, calculateVariation]);

  // Extraction des données de groupes de composants (memoized)
  const assetGroupsData = useMemo(() => {
    if (!rawData || !selectedAccount) return [];
    const accountData = rawData[selectedAccount.name];
    let allAssetGroups = accountData?.assetGroupsData?.assetGroups || [];

    // Greffer les grandeurs vidéo, chargées à part. Un groupe absent du second appel garde
    // zéro vue : la fabrique affichera alors un tiret pour le taux et le CPV, pas un 0 %.
    if (metriquesVideoActives) {
      allAssetGroups = allAssetGroups.map(groupe => ({
        ...groupe,
        ...TOTAUX_VIDEO_INITIAUX,
        ...(donneesVideo.parGroupe[`${groupe.name}_${(groupe.campaignIds || []).join('|')}`]
            || donneesVideo.parGroupe[groupe.name]
            || {})
      }));
    }

    // Appliquer le filtre par campagne si une campagne est sélectionnée
    if (selectedCampaignFilter) {
      allAssetGroups = allAssetGroups.filter(assetGroup => {
        // Vérifier si une des campagnes de ce groupe de composants correspond au filtre
        const campaignIdsArray = assetGroup.campaignIds || [];
        const campaignNamesArray = assetGroup.campaigns || [];

        const match = campaignIdsArray.includes(selectedCampaignFilter.id) ||
                     campaignIdsArray.includes(selectedCampaignFilter.campaignId) ||
                     campaignIdsArray.some(id => id?.toString() === selectedCampaignFilter.id?.toString()) ||
                     campaignNamesArray.includes(selectedCampaignFilter.name);

        return match;
      });
    }

    // Appliquer le filtre par type de campagne si un type est sélectionné
    if (selectedCampaignTypeFilter) {
      console.log('🎭 [ASSET-GROUPS] Filtrage par type de campagne:', selectedCampaignTypeFilter);

      // Récupérer les campagnes qui correspondent au type sélectionné
      // Les campagnes peuvent être dans rawData
      const allCampaigns = [];
      if (rawData && selectedAccount) {
        const accountData = rawData[selectedAccount.name];
        if (accountData?.campaigns) {
          allCampaigns.push(...accountData.campaigns);
        }
        if (accountData?.assetGroupsData?.campaigns) {
          allCampaigns.push(...accountData.assetGroupsData.campaigns);
        }
      }

      console.log('🎭 [ASSET-GROUPS] Campagnes disponibles:', allCampaigns.length);

      // Trouver les IDs des campagnes qui correspondent au type sélectionné
      const matchingCampaignIds = allCampaigns
        .filter(campaign => {
          const campaignType = campaign.advertisingChannelType || campaign.type || campaign.campaignType;
          const matches = campaignType === selectedCampaignTypeFilter.type ||
                         String(campaignType) === String(selectedCampaignTypeFilter.type) ||
                         campaignType === selectedCampaignTypeFilter.name ||
                         String(campaignType) === String(selectedCampaignTypeFilter.name);

          if (matches) {
            console.log('🎭 [ASSET-GROUPS] Campagne correspondante:', campaign.name, 'ID:', campaign.id, 'Type:', campaignType);
          }

          return matches;
        })
        .map(campaign => campaign.id || campaign.campaignId);

      console.log('🎭 [ASSET-GROUPS] IDs des campagnes correspondantes:', matchingCampaignIds);

      // Filtrer les groupes de composants qui appartiennent à ces campagnes
      allAssetGroups = allAssetGroups.filter(assetGroup => {
        const campaignIdsArray = assetGroup.campaignIds || [];
        const matches = campaignIdsArray.some(campaignId =>
          matchingCampaignIds.includes(campaignId) ||
          matchingCampaignIds.includes(String(campaignId))
        );

        if (matches) {
          console.log('🎭 [ASSET-GROUPS] Groupe de composants correspondant:', assetGroup.name, 'Campaign IDs:', campaignIdsArray);
        }

        return matches;
      });

      console.log('🎭 [ASSET-GROUPS] Groupes de composants après filtrage par type:', allAssetGroups.length);
    }

    // Appliquer le filtre par labels si des labels sont sélectionnés
    if (selectedLabelsFilter.length > 0) {
      console.log('🏷️ [ASSET-GROUPS] Filtrage par labels:', selectedLabelsFilter);
      allAssetGroups = allAssetGroups.filter(assetGroup => {
        // Vérifier si au moins une des campagnes du groupe correspond aux labels
        const campaignIdsArray = assetGroup.campaignIds || [];
        return campaignIdsArray.some(campaignId => campaignMatchesLabelsFilter(campaignId));
      });
      console.log('🏷️ [ASSET-GROUPS] Groupes de composants après filtrage par labels:', allAssetGroups.length);
    }

    return allAssetGroups;
  }, [rawData, selectedAccount, selectedCampaignFilter, selectedCampaignTypeFilter, selectedLabelsFilter, campaignMatchesLabelsFilter]);

  // Extraction des données de la période précédente (utilise les données chargées via API)
  const previousAssetGroupsData = useMemo(() => {
    console.log('🎭 [ASSET-GROUPS] Calcul previousAssetGroupsData:', {
      compareEnabled,
      loadedPreviousAssetGroupsLength: loadedPreviousAssetGroups.length,
      loadedPreviousAssetGroups: loadedPreviousAssetGroups.slice(0, 2)
    });
    if (!compareEnabled || !loadedPreviousAssetGroups.length) return [];

    let allAssetGroups = [...loadedPreviousAssetGroups];

    // Appliquer les mêmes filtres que pour les données actuelles
    if (selectedCampaignFilter) {
      allAssetGroups = allAssetGroups.filter(assetGroup => {
        const campaignIdsArray = assetGroup.campaignIds || [];
        const campaignNamesArray = assetGroup.campaigns || [];

        return campaignIdsArray.includes(selectedCampaignFilter.id) ||
               campaignIdsArray.includes(selectedCampaignFilter.campaignId) ||
               campaignIdsArray.some(id => id?.toString() === selectedCampaignFilter.id?.toString()) ||
               campaignNamesArray.includes(selectedCampaignFilter.name);
      });
    }

    if (selectedCampaignTypeFilter) {
      // Récupérer les campagnes qui correspondent au type sélectionné depuis rawData
      const allCampaigns = [];
      if (rawData && selectedAccount) {
        const accountData = rawData[selectedAccount.name];
        if (accountData?.campaigns) {
          allCampaigns.push(...accountData.campaigns);
        }
        if (accountData?.assetGroupsData?.campaigns) {
          allCampaigns.push(...accountData.assetGroupsData.campaigns);
        }
      }

      const matchingCampaignIds = allCampaigns
        .filter(campaign => {
          const campaignType = campaign.advertisingChannelType || campaign.type || campaign.campaignType;
          return campaignType === selectedCampaignTypeFilter.type ||
                 String(campaignType) === String(selectedCampaignTypeFilter.type) ||
                 campaignType === selectedCampaignTypeFilter.name ||
                 String(campaignType) === String(selectedCampaignTypeFilter.name);
        })
        .map(campaign => campaign.id || campaign.campaignId);

      allAssetGroups = allAssetGroups.filter(assetGroup => {
        const campaignIdsArray = assetGroup.campaignIds || [];
        return campaignIdsArray.some(campaignId =>
          matchingCampaignIds.includes(campaignId) ||
          matchingCampaignIds.includes(String(campaignId))
        );
      });
    }

    if (selectedLabelsFilter.length > 0) {
      allAssetGroups = allAssetGroups.filter(assetGroup => {
        const campaignIdsArray = assetGroup.campaignIds || [];
        return campaignIdsArray.some(campaignId => campaignMatchesLabelsFilter(campaignId));
      });
    }

    return allAssetGroups;
  }, [compareEnabled, loadedPreviousAssetGroups, rawData, selectedAccount, selectedCampaignFilter, selectedCampaignTypeFilter, selectedLabelsFilter, campaignMatchesLabelsFilter]);


  // Fonction de tri memoized
  const sortedAssetGroups = useMemo(() => {
    if (!assetGroupsData.length || !sortConfig.key) return assetGroupsData;

    const sorted = [...assetGroupsData];
    sorted.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'name') {
        // Pour les noms, tri alphabétique
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Pour les nombres
      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;

      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  }, [assetGroupsData, sortConfig]);

  // Calcul des totaux memoized
  const totals = useMemo(() => {
    if (!assetGroupsData.length) return {};

    const baseTotals = sortedAssetGroups.reduce((acc, group) => ({
      impressions: acc.impressions + (group.impressions || 0),
      clicks: acc.clicks + (group.clicks || 0),
      cost: acc.cost + (group.cost || 0),
      conversions: acc.conversions + (group.conversions || 0),
      conversionsValue: acc.conversionsValue + (group.conversionsValue || 0),
      ...cumulerVideo(acc, group)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, ...TOTAUX_VIDEO_INITIAUX });

    // Calculer les métriques dérivées
    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [sortedAssetGroups, assetGroupsData]);

  // Calcul des totaux de la période précédente
  const previousTotals = useMemo(() => {
    if (!compareEnabled || !previousAssetGroupsData.length) return null;

    const baseTotals = previousAssetGroupsData.reduce((acc, group) => ({
      impressions: acc.impressions + (group.impressions || 0),
      clicks: acc.clicks + (group.clicks || 0),
      cost: acc.cost + (group.cost || 0),
      conversions: acc.conversions + (group.conversions || 0),
      conversionsValue: acc.conversionsValue + (group.conversionsValue || 0),
      ...cumulerVideo(acc, group)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, ...TOTAUX_VIDEO_INITIAUX });

    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [compareEnabled, previousAssetGroupsData]);

  // Configuration de pagination
  const itemsPerPage = 10;
  const totalAssetGroups = sortedAssetGroups.length;
  const totalPages = Math.ceil(totalAssetGroups / itemsPerPage);

  // Données paginées memoized
  const paginatedAssetGroups = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalAssetGroups);
    return sortedAssetGroups.slice(startIndex, endIndex);
  }, [sortedAssetGroups, currentPage, itemsPerPage, totalAssetGroups]);

  // Handler de tri memoized
  const handleSort = useCallback((key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1); // Réinitialiser à la première page
  }, [sortConfig]);

  // Handler de pagination memoized
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(Math.max(1, Math.min(totalPages, newPage)));
  }, [totalPages]);

  // Ne pas rendre si pas de données
  if (!assetGroupsData.length) return null;

  // Pour les clients : masquer complètement si le module est fermé
  if (userRole === 'CLIENT' && !isVisible) {
    return null;
  }

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalAssetGroups);

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4><Layers size={16} />&nbsp;Groupes de composants (Performance Max)</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Groupes de composants"
            />
          ) : (
            <button
              onClick={onToggleVisibility}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {isVisible && (
        <>
          {/* Indicateur de chargement des données de comparaison */}
          {compareEnabled && (loadingComparison || loadingPrevious) && (
            <div className="loading-message" style={{ textAlign: 'center', padding: '20px' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 10px' }}></div>
              Chargement des données de comparaison...
            </div>
          )}
          {(!compareEnabled || (!loadingComparison && !loadingPrevious)) && (
          <>
          {/*
            * Ce que les groupes de composants ne peuvent pas porter.
            *
            * Quand Performance Max fabrique lui-même la vidéo, faute d'en recevoir une, ses
            * vues n'appartiennent à aucun groupe. Les taire ferait lire « ce groupe n'a pas
            * de vidéo » là où il en a diffusé une. On annonce donc le reliquat plutôt que de
            * laisser croire le total complet.
            */}
          {metriquesVideoActives && donneesVideo.nonAttribuee?.incomplet && (
            <div style={{
              margin: '0 0 12px',
              padding: '10px 14px',
              borderRadius: '8px',
              border: '1px solid #fde68a',
              background: '#fffbeb',
              color: '#92400e',
              fontSize: '0.8125rem',
              lineHeight: 1.5
            }}>
              <strong>{formatNumber(donneesVideo.nonAttribuee.views)} vues</strong>
              {' '}sur {formatNumber(donneesVideo.nonAttribuee.totalCampagnes)} ne figurent dans aucune ligne
              ci-dessous. {donneesVideo.nonAttribuee.raison}
            </div>
          )}

          <div className="campaigns-table-wrapper">
            <table
              key={selectedMetrics.filter(m => m.selected).map(m => m.key).join(',')}
              className="campaigns-table"
              style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'collapse' }}
            >
              <thead>
                <tr>
                  <th onClick={() => handleSort('name')} className="sortable">
                    Groupe de composants {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                  {/* Rendu dynamique des colonnes selon l'ordre configuré */}
                  {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                    const config = metricConfig[metricKey];
                    return (
                      <th key={metricKey} onClick={() => handleSort(config.sortKey)} className="sortable numeric">
                        {config.label} {sortConfig.key === config.sortKey && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {paginatedAssetGroups.map((group, index) => {
                  // Trouver le groupe correspondant dans les données de la période précédente
                  const prevGroup = compareEnabled && previousAssetGroupsData.length > 0
                    ? previousAssetGroupsData.find(p => p.name === group.name || p.id === group.id)
                    : null;
                  const hasComparison = !!prevGroup;

                  return (
                    <tr key={index}>
                      <td title={group.name} style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {group.isDelivering !== undefined && (
                            <span
                              style={{
                                display: 'inline-block',
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: group.isDelivering ? '#22c55e' : '#ef4444',
                                marginRight: '8px',
                                flexShrink: 0
                              }}
                              title={group.isDelivering ? 'Actif' : 'Inactif'}
                            />
                          )}
                          <span>{group.name}</span>
                        </div>
                        {(group.campaignName || (group.campaigns && group.campaigns.length > 0)) && (
                          <div style={{ fontSize: '0.8em', color: '#888', marginTop: '2px', marginLeft: group.isDelivering !== undefined ? '16px' : '0' }}>
                            {group.campaignName || group.campaigns?.join(', ')}
                          </div>
                        )}
                      </td>
                      {/* Rendu dynamique des cellules selon l'ordre configuré */}
                      {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                        const config = metricConfig[metricKey];
                        const className = config.getClassName ? `numeric ${config.getClassName(group)}` : 'numeric';
                        return (
                          <td key={metricKey} className={className} style={{ verticalAlign: 'top' }}>
                            {config.renderCell(group, prevGroup || {}, hasComparison)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>

              <tfoot>
                {/* Ligne de totaux */}
                <tr className="totals-row">
                  <td><strong>TOTAL</strong></td>
                  {/* Rendu dynamique des totaux selon l'ordre configuré */}
                  {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                    const config = metricConfig[metricKey];
                    const className = config.getTotalClassName ? `numeric ${config.getTotalClassName(totals)}` : 'numeric';
                    return (
                      <td key={metricKey} className={className} style={{ verticalAlign: 'top' }}>
                        {config.renderTotal(totals, previousTotals)}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Contrôles de pagination */}
          {totalPages > 1 && (
            <div className="pagination-controls">
              <div className="pagination-info">
                <span>
                  Affichage {startIndex + 1}-{Math.min(endIndex, sortedAssetGroups.length)} sur {sortedAssetGroups.length} groupes de composants
                </span>
              </div>
              <div className="pagination-buttons">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  ← Précédent
                </button>
                <span className="page-numbers">
                  Page {currentPage} sur {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Suivant →
                </button>
              </div>
            </div>
          )}
          </>
          )}
        </>
      )}

      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId="asset_groups"
        accountId={selectedAccount?.id || 'global'}
        componentTitle="Groupes de composants"
      />
    </div>
  );
});

AssetGroupsTable.displayName = 'AssetGroupsTable';

export default AssetGroupsTable;
